import { CodeEditor } from '@acrodata/code-editor';
import { animate, style, transition, trigger } from '@angular/animations';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  input,
  Input,
  model,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef,
  ViewChild,
  viewChild,
  WritableSignal,
} from '@angular/core';

import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { indentWithTab } from '@codemirror/commands';
import { type Extension } from '@codemirror/state';
import { keymap } from '@codemirror/view';
import { translate, translateObject } from '@jsverse/transloco';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { ReplaySubject, Subject } from 'rxjs';

import { debounceTime, skip, takeUntil, tap } from 'rxjs/operators';

import { v4 as UUIDv4 } from 'uuid';

import { TabObject } from '@inmoment/the-kitchen';

import { SimpleModalOptions } from 'app/components/xi-components/lex-xi-simple-modal/lex-xi-simple-modal.component';
import { lexQueryErrorTooltip, showTooltipAtPosition } from 'app/editor/codemirror.errorTooltip';
import { searchHighlight } from 'app/editor/codemirror.searchHighlight';
import { lexQueryLanguage } from 'app/editor/lexquery.language';
import { lexQueryExtensions as lexQueryThemeExtensions } from 'app/editor/lexquery.theme';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { DocumentAnalysis } from 'app/providers/storage-api/analysis.service';
import {
  ConfigurationTesterService,
  CTDatasetDescriptor,
  CTDatasetNodes,
  CTDatasetRequest,
  CTEvaluationResponse,
} from 'app/providers/storage-api/configurations.service';
import { LoggerService } from 'app/services/logger.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';
import { SimpleDialogButtonColor } from '../../../../components/simple-dialog.component';
import {
  ConfigurationItem,
  ConfigurationItemResult,
  ConfigurationItemSortParams,
  SemantriaConfiguration,
} from '../../../../providers/semantria-api/configuration.service';

import { SemantriaDocumentRequest } from '../../../../providers/semantria-api/evaluation-service';
import { AlertService } from '../../../../services/alert.service';
import { DialogService } from '../../../../services/dialog.service';

import { LocalStorageService } from '../../../../services/local-storage.service';
import { ConfigurationLayer } from '../configuration-layers/configuration-layers.component';
import { CTEvaluationResultStatusInfo } from '../configuration-tester-document-list/configuration-tester-document-list.component';
import { TestDocumentGeneratorDialogComponent } from '../test-document-generator-dialog/test-document-generator-dialog.component';
import { configItemNameValidatorFactory } from '../../configuration-utils';

import { ConfigurationItemListOptions } from '../../models/configuration-item-list-options.model';
import { ConfigurationItemListOptionsService } from '../../services/configuration-item-list-options.service';
import { TestDocumentGeneratorService } from '../../services/test-document-generator.service';

export interface QueryPropHandler<S> {
  parse: (str: string) => S;
  serialize: (q: S) => string;
  includes: (prop: S, str: string) => boolean;
}

export class StringQueryHandler implements QueryPropHandler<string> {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  parse = (str: string) => str;

  serialize = (q: string) => q;

  includes = (prop: string, str: string) => prop && prop.toUpperCase().includes(str);
}

export class ArrayQueryHandler implements QueryPropHandler<Array<string>> {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  parse = (str: string) => (str && str.split('\n')) || [];

  serialize = (q: Array<string>) => (q && q.join('\n')) || '';

  includes = (prop: Array<string>, str: string) =>
    prop && prop.some((propStr) => propStr.toUpperCase().includes(str));
}

export class NoopQueryHandler implements QueryPropHandler<any> {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  parse = (str: string) => str;

  serialize = (q: string) => q;

  includes = (_prop: string, _str: string) => false;
}

export interface EvaluationConfigUpdateResponse {
  existingConfigItem: ConfigurationItem;
  updatedConfigItem: ConfigurationItem;
  wasUpdated: boolean;
  error: any;
}

export declare class CTTab extends TabObject {
  allowedFragments: string[];
}

export interface CTState {
  selectedTab: CTTabId;
  availableTabs: CTTabId[];
  collectionSelectVisible?: boolean;
  csvUploadVisible?: boolean;
  csvUploadNotes?: string[];
  csvValidator: (fileUpload, importedItem) => { isValid: boolean; message: string };
  loading: boolean;
  evaluation?: {
    completed?: boolean;
    expired?: boolean;
  };
  dataset?: {
    descriptor: CTDatasetDescriptor;
    nodes?: CTDatasetNodes;
    icon?: string;
  };
}

type CTTabId = 'use-dataset' | 'create-documents';

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-item-editor',
  templateUrl: './configuration-item-editor.component.html',
  styleUrls: ['./configuration-item-editor.component.scss'],
  providers: [ConfigurationItemListOptionsService],
  animations: [
    trigger('editorTrigger', [
      transition(':enter', [
        style({ left: '100%' }),
        animate('0.5s ease-in-out', style({ left: 0 })),
      ]),
      transition(':leave', [animate('0.5s ease-in-out', style({ left: '100%' }))]),
    ]),
    trigger('searchControlTrigger', [
      transition(':enter', [
        style({ maxWidth: 0 }),
        animate('0.2s linear', style({ maxWidth: '40px' })),
      ]),
      transition(':leave', [animate('0.2s linear', style({ width: 0 }))]),
    ]),
  ],
  standalone: false,
})
export class ConfigurationItemEditorComponent<T extends ConfigurationItem = ConfigurationItem>
  implements OnInit, OnChanges, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly configuration = input<SemantriaConfiguration>();
  readonly configurationLayers = input<ConfigurationLayer[]>();
  readonly itemTypeTitle = input<string>();
  readonly searchPlaceholder = input<string>();
  readonly canSearchBy = input<boolean>();
  readonly queryPlaceholder = input<string>(
    translate('configuration-item-editor-component.query-placeholder')
  );
  // Encountered some unresolvable issues with signalized callback inputs(testing), so for now we are using the old approach on those
  @Input() loadItemsApi: (
    configId,
    pageNum?,
    pageSize?,
    searchString?,
    searchFields?,
    sortParams?: ConfigurationItemSortParams
  ) => Promise<ConfigurationItemResult<T>>;
  @Input() createItemApi: (configId, item) => Promise<T>;
  @Input() saveItemApi: (configId, item) => Promise<T>;
  @Input() deleteItemApi: (configId, itemId) => Promise<any>;
  @Input() deleteAllItemsApi: (configId) => Promise<string[]>;
  @Input() evaluateItemApi: (configId, documents) => Promise<any>;
  @Input() evaluateDatasetApi?: (configId, documents) => Promise<any>;
  @Input() getEvaluationConfigApi: (configId) => Promise<string>;
  @Input() getHighlightTextString: (
    doc: DocumentAnalysis,
    documentText: string,
    itemValues: string[],
    lookInSections?: boolean,
    sectionId?: string
  ) => string;
  @Input() getEvaluationItemHighlightValue: (item) => string[];
  @Input() getEvaluationItemByClonedFrom: (configId, clonedFrom) => Promise<T>;
  @Input() deleteEvaluationConfigApi: (configId) => Promise<void>;

  readonly rowExtraTemplate = input<TemplateRef<any>>();
  readonly editorTemplate = input<TemplateRef<any>>();
  readonly isEditorTemplateValid = model<boolean>(true);
  readonly editorTemplateHasChanges = model<boolean>();
  readonly testingEnabled = input<boolean>();
  readonly taxonomyItemUsage = input<string[]>();

  readonly selectedItemId = input<string>();
  readonly selectedItemName = input<string>();
  readonly selectedItemType = input<string>(); // may can be deleted since it is not used here nor in template
  readonly selectedItem = model<T | undefined>();
  readonly queryPropName = input<string>('query');
  readonly namePropName = input<string>('name');
  readonly nameEditable = model<boolean>(true);
  readonly queryEditable = input<boolean>(true);
  readonly itemsEditable = input<boolean>(true);
  readonly syntaxHelpLink = input<string>();

  readonly fragment = input<string>();
  readonly addPopoverTemplate = input<TemplateRef<any>>();

  readonly hasOptionsPopout = input<boolean>();

  readonly selectedItemChange = output<T>();
  readonly itemsLoaded = output<T[]>();
  readonly saving = output<boolean>();
  readonly itemAdded = output<T>();
  readonly itemUpdated = output<T>();
  readonly itemsDeleted = output<T[]>();
  readonly allItemsDeleted = output<string[]>();

  itemListScroll = viewChild<ElementRef>('itemListScroll');
  itemSortPopover = viewChild<NgbPopover>('itemSortPopover');
  addButtonPopover = viewChild<NgbPopover>('addButtonPopover');
  statusFilterPopover = viewChild<NgbPopover>('statusFilterPopover');
  virtualScroll = viewChild(CdkVirtualScrollViewport);
  codeEditor = viewChild(CodeEditor);
  @ViewChild('optionsPopoutButton') optionsPopoutButton: ElementRef;

  readonly configTesterDatasetMaxRows = 5000;
  readonly configTesterTabs: CTTab[] = [
    {
      id: 'use-dataset',
      title: translate('configuration-item-editor-component.use-dataset'),
      allowedFragments: ['queries'],
    },
    {
      id: 'create-documents',
      title: translate('configuration-item-editor-component.create-documents'),
      allowedFragments: ['*'],
    },
  ];

  // CM6: LexQuery syntax highlighting + theme (+ optional search highlighting)
  lexQueryExtensions: Extension[] = [
    keymap.of([indentWithTab]),
    lexQueryErrorTooltip,
    lexQueryLanguage,
    ...lexQueryThemeExtensions,
  ];
  configTesterState: CTState = {
    selectedTab: 'create-documents',
    availableTabs: [],
    loading: false,
    csvUploadNotes: [
      translate('configuration-item-editor-component.upload-csv-file-note', {
        maxRows: Utils.addThousandSeparator(this.configTesterDatasetMaxRows),
      }),
    ],
    csvValidator: () => {
      return {
        isValid: true,
        message: null,
      };
    },
  };

  onConfigurationTesterTabChange(tabId: CTTabId) {
    this.configTesterState = {
      ...this.configTesterState,
      selectedTab: tabId,
      csvUploadVisible: false,
      collectionSelectVisible: false,
    };

    // Reevaluate disabled state
    this.isTestButtonDisabled$.next(this.isTestButtonDisabled);
  }

  showSelectCollectionDialog() {
    // Prevent changing datasource while test is running
    if (this.isEvaluationInProgress || this.configTesterState.loading) {
      return;
    }
    this.configTesterState = {
      ...this.configTesterState,
      collectionSelectVisible: true,
    };
  }

  onSelectCollectionDialogClosed(result) {
    this.configTesterState = {
      ...this.configTesterState,
      collectionSelectVisible: false,
    };

    if (result?.confirmed && result?.selectedCollection) {
      this.saveCTDataset({
        projectId: result.selectedCollection.projectId,
        collectionName: result.selectedCollection.collectionName,
        configId: this.configuration().id,
      });
    }
  }

  showCSVFileUploadDialog() {
    // Prevent changing datasource while test is running
    if (this.isEvaluationInProgress || this.configTesterState.loading) {
      return;
    }
    this.configTesterState = {
      ...this.configTesterState,
      csvUploadVisible: true,
    };
  }

  onCsvFileUploadDialogClosed(result) {
    if (!result) {
      this.configTesterState = {
        ...this.configTesterState,
        csvUploadVisible: false,
      };
    }
  }

  async onCsvFilesConfirmed(result) {
    this.configTesterState = {
      ...this.configTesterState,
      csvUploadVisible: false,
    };

    if (result?.files?.length) {
      // Only one file allowed to be uploaded
      this.saveCTDataset({
        file: result.files[0],
        firstRecordAsHeader: result.firstRecordAsHeader,
        configId: this.configuration().id,
      });
    }
  }

  async saveCTDataset(request: CTDatasetRequest) {
    // Prevent changing datasource while test is running
    if (this.isEvaluationInProgress) {
      this.alertService.showToast(
        translate('configuration-item-editor-component.cannot-change-datasource-while-test-running')
      );
      return;
    }
    const saveDatasetFunction = async () => {
      try {
        this.configTesterState = {
          ...this.configTesterState,
          loading: true,
        };
        const datasetDescriptor = await this.configurationsService.saveDataset(request);
        this.setCTDatasetDescriptor(datasetDescriptor);
        // Clear existing test data after saving new dataset
        try {
          await this.configurationsService.clearResults({ configId: this.configuration().id });

          // Update the UI to reflect that results have been cleared
          this.onCTDocumentListResultsCleared();

          // Show a toast that test data was cleared
          this.alertService.showToast(
            translate('configuration-item-editor-component.test-data-cleared')
          );
        } catch (clearError) {
          this.logger.error('Failed to clear test data:', { clearError });
        }
      } catch (error) {
        this.alertService.showErrorToast(
          translate('configuration-item-editor-component.failed-to-save-dataset'),
          error
        );
      } finally {
        this.configTesterState = {
          ...this.configTesterState,
          loading: false,
        };
      }
    };

    // Show confirmation dialog if there is already a dataset
    if (!!this.configTesterState.dataset) {
      const translations = translateObject(
        'configuration-item-editor-component.datasource-change-confirmation'
      );
      this.dialogService
        .showWarningPopup({
          title: translations['title'],
          content: translations['content'],
          confirmTitle: translations['confirm-button'],
          confirmColor: SimpleDialogButtonColor.red,
        })
        .afterClosed()
        .subscribe((result) => {
          if (result) {
            saveDatasetFunction();
          }
        });
    } else {
      // Else simply save the dataset
      saveDatasetFunction();
    }
  }

  setCTDatasetDescriptor(descriptor: CTDatasetDescriptor) {
    let dataset = undefined;
    if (descriptor) {
      dataset = {
        descriptor,
        icon: descriptor.type === 'COLLECTION' ? 'collections' : 'document_ext_csv',
      };
      this.leftSplitSize = 50;
      this.rightSplitSize = 50;
    } else {
      this.leftSplitSize = 35;
      this.rightSplitSize = 65;
    }
    this.configTesterState = {
      ...this.configTesterState,
      dataset: dataset,
    };
    this.isTestButtonDisabled$.next(this.isTestButtonDisabled);
  }

  setCTAvailableTabs() {
    this.configTesterState = {
      ...this.configTesterState,
      availableTabs: [
        ...this.configTesterTabs
          .filter(
            (tab) =>
              tab.allowedFragments.includes(this.fragment()) || tab.allowedFragments.includes('*')
          )
          .map((tab) => tab.id as CTTabId),
      ],
    };
  }

  onCTDocumentListResultsCleared() {
    this.configTesterState = {
      ...this.configTesterState,
      evaluation: undefined,
    };
  }

  onCTDocumentListResultsArrived(evaluation: CTEvaluationResponse) {
    this.configTesterState = {
      ...this.configTesterState,
      evaluation: {
        completed: evaluation?.status === 'COMPLETED',
        expired: !!evaluation?.expired,
      },
    };
    this.isEvaluationInProgress = evaluation?.status === 'RUNNING';
  }

  private updateEditorSearchHighlight(nextSearchValue?: string) {
    // Base extensions without search highlighting
    const base: Extension[] = [
      keymap.of([indentWithTab]),
      lexQueryErrorTooltip,
      lexQueryLanguage,
      ...lexQueryThemeExtensions,
    ];
    const effectiveSearch = nextSearchValue !== undefined ? nextSearchValue : this.searchValue;
    const shouldHighlight =
      !!effectiveSearch && (this.searchType === 'all' || this.searchType === 'query');

    // Add search highlighting extension if needed
    this.lexQueryExtensions = shouldHighlight ? [...base, searchHighlight(effectiveSearch)] : base;
  }

  readonly itemSortValues = [
    {
      value: 'name',
      label: translate('configuration-item-editor-component.sort-name-label'),
      sortFn: (a, b) => this.localeSort(a, b),
      apiSort: { sort: 'name' } as ConfigurationItemSortParams,
    },
    {
      value: 'modification_date',
      label: translate('configuration-item-editor-component.sort-date-label'),
      sortFn: (a, b) =>
        new Date(b.modification_date).getTime() - new Date(a.modification_date).getTime(),
      apiSort: { sort: 'last_updated', reverseOrder: true } as ConfigurationItemSortParams,
    },
  ];

  readonly maxTestDocumentCount = 10;

  configItems: T[];
  filteredConfigItems: T[];
  pageSize = 100;
  itemSort: {
    value: string;
    label: string;
    sortFn: (a, b) => number;
    apiSort: ConfigurationItemSortParams;
  } = this.itemSortValues[0];
  //Translated Item Type for UI display. Changed on init/changes
  translatedItemTypeTitle: string;

  search$ = new Subject<string>();
  searchValue = '';
  currentSearchIndex = signal(-1);
  searchType: 'all' | 'name' | 'query' = 'all';

  testMode: boolean;
  editorMode: 'create' | 'update' | 'none';
  inlineEditorMode: 'view' | 'edit' = 'view';
  canEditItem: boolean;

  selectedItemLayer: ConfigurationLayer;
  originalItem: T;
  query: string;
  originalQuery: string;
  itemName: UntypedFormControl;
  originalName = '';
  queryPropHandler: QueryPropHandler<any>;
  private addButtonData: any;

  isLoading = false;
  isLoadingPage = false;
  isSaving: boolean;
  itemCheckInProgress: boolean;
  isEvaluationInProgress = false;
  isClearingCache = false;

  rightSplitSize = 65;
  leftSplitSize = 35;
  testDocuments: {
    value: string;
    documentId: string;
    htmlContent: string;
    hasBeenTested: boolean;
    canRephrase?: boolean;
    rephraseInProgress?: boolean;
  }[] = [];
  page = 0;
  totalPages = 0;
  totalItems = 0;
  isTestButtonDisabled$ = new ReplaySubject<boolean>(1);

  evaluationConfigID: string;
  evaluationResults = [];
  createdEvaluationItemId: string;
  failedToCreateEvalConfig = false;
  failedEvalConfigMessage: string;
  private session: Session;

  private unsub$ = new Subject<void>();

  private scrollInterval;

  scrollViewportItemSize = 48;
  public showStemmedDocumentModal = false;
  public stemmedDocumentData: SimpleModalOptions;

  hasTestDocumentGenerationPermission = false;
  hasRephrasePermission = false;

  isOptionsPopoutVisible = false;
  configurationItemListOptions: ConfigurationItemListOptions;
  private localStorageService = inject(LocalStorageService);
  private isFullscreen: WritableSignal<boolean> = signal<boolean>(false);
  isTesterFullscreen = false;

  availableStatuses: string[] = [];
  selectedStatuses: string[] = [];

  readonly RESULT_STATUS_INFO: CTEvaluationResultStatusInfo = {
    HIT: { icon: 'circle_check_filled', color: 'success' },
    DIDNT_HIT: { icon: 'cancel_circle_filled', color: 'error' },
    ADDED: { icon: 'add_circle_filled', color: 'success' },
    REMOVED: { icon: 'cancel_circle_filled', color: 'error' },
    DIFFERENT_HIT: { icon: 'circle_check_filled', color: 'success' },
    UNCHANGED: { icon: 'circle_check_filled', color: 'success' },
    ERROR: { icon: 'document_alert', color: 'error' },
  };

  onAvailableStatusesChanged(statuses: string[]) {
    this.availableStatuses = statuses ?? [];
  }

  isStatusSelected(status: string): boolean {
    return this.selectedStatuses.includes(status);
  }

  toggleStatus(status: string) {
    if (this.isStatusSelected(status)) {
      this.selectedStatuses = this.selectedStatuses.filter((s) => s !== status);
    } else {
      this.selectedStatuses = [...this.selectedStatuses, status];
    }
    // Need to trigger change detection or signal update if using signals, but standard array assignment works with CD.
  }

  toggleTesterFullscreen() {
    this.isTesterFullscreen = !this.isTesterFullscreen;
  }

  getStatusFilterButtonText(t: any): string {
    if (this.selectedStatuses.length === 0) {
      return t('filter-status');
    }
    const statusLabels = this.selectedStatuses.map((s) => t('status-value.' + s));
    const joined = statusLabels.join(', ');
    if (joined.length > 20) {
      return t('n-statuses', { count: this.selectedStatuses.length });
    }
    return joined;
  }

  constructor(
    private alertService: AlertService,
    private dialogService: DialogService,
    private authService: AuthService,
    private groupService: GroupService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private cdRef: ChangeDetectorRef,
    private testDocumentGeneratorService: TestDocumentGeneratorService,
    private configurationItemListOptionsService: ConfigurationItemListOptionsService,
    private configurationsService: ConfigurationTesterService,
    private logger: LoggerService
  ) {
    this.itemName = this.formBuilder.control('');

    this.isTestButtonDisabled$.pipe(takeUntil(this.unsub$)).subscribe(); //initial subscribtion

    this.configurationItemListOptions = this.configurationItemListOptionsService.get();
  }

  async ngOnInit() {
    if (this.configurationItemIsQueryTopic()) {
      this.testDocumentGeneratorService
        .hasPermission()
        .then((hasPermission) => (this.hasTestDocumentGenerationPermission = hasPermission));

      this.testDocumentGeneratorService
        .hasRephrasePermission()
        .then((hasPermission) => (this.hasRephrasePermission = hasPermission));
    }

    this.search$
      .pipe(
        takeUntil(this.unsub$),
        debounceTime(250),
        tap((val) => (this.searchValue = val))
      )
      .subscribe(async () => {
        if (this.currentSearchIndex() === -1) {
          this.page = 0;
          this.totalPages = 0;
          this.configItems = [];
          this.filteredConfigItems = [];
          this.configItems = await this.loadItems(this.loadItemsApi);
          this.filteredConfigItems = [...this.configItems];

          if (this.filteredConfigItems.length) {
            this.currentSearchIndex.update(() => 0);

            const idx = this.filteredConfigItems.findIndex((i) => i.id === this.selectedItem()?.id);
            if (idx > -1) {
              this.currentSearchIndex.update(() => idx);
            }
          }
        }

        this.handleSearchResult();
      });

    this.addEmptyDocument();
    this.session = await this.authService.getSession();
    this.canEditItem = !this.authService.isReadOnly && !!this.itemsEditable();
    if (!this.itemsEditable()) {
      this.nameEditable.set(false);
    }
    if (this.selectedItem() && this.selectedItem()[this.namePropName()]) {
      this.itemName = this.formBuilder.control(this.selectedItem()[this.namePropName()]);
    } else {
      this.itemName = this.formBuilder.control('');
    }

    try {
      if (this.getEvaluationConfigApi && this.canEditItem) {
        this.evaluationConfigID = await this.getEvaluationConfigApi(this.configuration());
      }
    } catch (error) {
      // We failed to create the evaluation config.  Log the error but don't lock out the buttons
      // They should attempt the creation again when pressed.
      this.logger.error('Failed to create evaluation config', { error });
    }

    this.route.fragment.pipe(skip(1), takeUntil(this.unsub$)).subscribe((next) => {
      if (next === this.fragment() && this.selectedItem && this.itemListScroll()) {
        setTimeout(() => {
          this.scrollToItem(this.selectedItem());
          this.virtualScroll().checkViewportSize();
        });
      }
    });

    this.setCTAvailableTabs();
  }

  async ngOnChanges(changes) {
    if (!this.session) {
      this.session = await this.authService.getSession();
    }

    if (changes.configuration) {
      if (this.configuration().removed) {
        return;
      }

      this.isLoading = true;

      this.configItems = [];

      if (!this.testingEnabled() && this.testMode) {
        this.onEditModeClicked(false);
      }

      if (this.hasOptionsPopout()) {
        this.applyConfigurationItemListOptions();
      }

      try {
        this.isLoadingPage = true;
        this.configItems = await this.loadItems(this.loadItemsApi);
        this.filteredConfigItems = [...this.configItems];
        this.selectItem(this.configItems[0]);
        this.itemsLoaded.emit(this.configItems);
        this.virtualScroll()?.checkViewportSize();
        this.isLoadingPage = false;

        if (this.authService.isReadOnly) {
          setTimeout(() => {
            this.itemName.disable({ emitEvent: false });
          }, 1000);
        }

        this.isLoading = false;
      } catch (error) {
        this.logger.error('Unknown error happened', { error });

        this.isLoading = false;
      }
    }

    if (
      changes.selectedItemId &&
      changes.selectedItemId.currentValue &&
      changes.selectedItemId.currentValue !== changes.selectedItemId.previousValue
    ) {
      if (this.selectedItemName()) {
        this.onSearchChanged(this.selectedItemName());
      } else {
        const item = this.configItems.find((t) => t.id === this.selectedItemId());
        if (item) {
          setTimeout(() => this.checkAndSelectItem(item, true));
        } else {
          this.alertService.showToast(
            translate('configuration-item-editor-component.item-does-not-exist', {
              itemTypeTitle: this.itemTypeTitle(),
              selectedItemName: this.selectedItemName(),
            })
          );
        }
      }
    }
    if (changes.itemTypeTitle) {
      this.translatedItemTypeTitle = translate(
        'analytic-items.' + this.itemTypeTitle().replace(/\s+/g, '-').toLowerCase()
      );
    }
  }

  ngOnDestroy() {
    this.isTestButtonDisabled$.complete();
    this.unsub$.next();
    this.unsub$.complete();

    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
    }
  }

  configurationItemIsQueryTopic(): boolean {
    return this.itemTypeTitle() === 'Query Topic';
  }

  async loadItems(apiCall) {
    let items = [];

    const nextPage = await apiCall(
      this.configuration().id,
      this.page,
      this.pageSize,
      this.searchValue,
      this.searchType,
      this.itemSort.apiSort
    );
    this.totalPages = nextPage.total_pages;
    this.totalItems = nextPage.total_elements;

    items.push(...nextPage.data);

    if (this.hasOptionsPopout() && !this.configurationItemListOptions.showHidden) {
      items = items.filter((i) => !i.hidden);
    }

    return items;
  }

  async onSearchFocused($event) {
    if (!this.itemCheckInProgress) {
      $event.stopImmediatePropagation();

      const proceed = await new Promise((resolve) => this.checkItemChanges(resolve));

      if (!proceed && $event.type === 'focus') {
        setTimeout(() => $event.target.blur());
      } else if (proceed && $event.type === 'mousedown') {
        setTimeout(() => $event.target.click());
      }
    }
  }

  onSearchTypeChanged() {
    this.currentSearchIndex.update(() => -1);

    this.search$.next(this.searchValue);
    this.updateEditorSearchHighlight(this.searchValue);
  }

  onSearchChanged($event) {
    this.currentSearchIndex.update(() => -1);
    this.searchValue = $event;
    this.search$.next($event);
    this.updateEditorSearchHighlight($event);
  }

  searchPrevious() {
    this.currentSearchIndex.update((prevValue) => prevValue - 1);
    if (this.currentSearchIndex() === -1) {
      this.currentSearchIndex.update(() => this.filteredConfigItems.length - 1);
    }

    this.handleSearchResult();
  }

  searchNext() {
    this.currentSearchIndex.update((prevValue) => prevValue + 1);
    if (this.currentSearchIndex() === this.filteredConfigItems.length) {
      this.currentSearchIndex.update(() => 0);
    }

    this.handleSearchResult();
  }

  private handleSearchResult() {
    const item = this.filteredConfigItems[this.currentSearchIndex()];

    if (item && this.selectedItem() && item === this.selectedItem()) {
      this.scrollToItem(item);
    } else {
      this.selectItem(item, true);
    }
  }

  onSortItemClicked(option) {
    if (this.itemSort !== option) {
      this.itemSort = option;
      this.onSearchTypeChanged();
    }

    this.itemSortPopover().close();
  }

  private scrollToItem(item: T) {
    if (item) {
      const selectedIndex = this.filteredConfigItems.findIndex((elem) => elem.id === item.id);
      if (this.virtualScroll() && selectedIndex > -1) {
        setTimeout(() => {
          this.virtualScroll().scrollToIndex(selectedIndex);
        });
      }
    }
  }

  triggerAddButtonPopover(addPopoverData) {
    if (this.addButtonPopover() && !this.addButtonPopover().isOpen()) {
      this.addButtonData = addPopoverData;
      this.addButtonPopover().open({ $implicit: addPopoverData });
      document.addEventListener(
        'click',
        () => {
          this.addButtonPopover().close();
        },
        { once: true }
      );
    }
  }

  addItem() {
    const newItem = {
      id: undefined,
      name: '',
      samples: [],
    };
    newItem[this.namePropName()] = '';
    newItem[this.queryPropName()] = '';

    if (this.addButtonPopover() && this.addButtonPopover().isOpen()) {
      newItem[this.namePropName()] = this.addButtonData;
      if (this.queryEditable) {
        newItem[this.queryPropName()] = this.addButtonData;
      }
      this.addButtonPopover().close();
    }

    this.checkAndSelectItem(newItem);
    this.scrollToItem(this.filteredConfigItems[0]);
  }

  deleteAllItems() {
    this.dialogService
      .showWarningPopup({
        title: translate('configuration-item-editor-component.delete-title'),
        content: translate('configuration-item-editor-component.delete-content', {
          totalItems: this.totalItems,
          itemTypeTitle: this.itemTypeTitle(),
        }),
        confirmTitle: translate('configuration-item-editor-component.delete-confirm'),
        cancelTitle: translate('configuration-item-editor-component.cancel'),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.isSaving = true;
          this.isLoading = true;
          this.itemName.disable();
          this.saving.emit(this.isSaving);

          try {
            const deleteResult = await this.deleteAllItemsApi(this.configuration().id);

            if (deleteResult.length) {
              this.alertService.showToast(
                translate('configuration-item-editor-component.delete-failed-taxonomy', {
                  count: deleteResult.length,
                  itemTypeTitle: this.itemTypeTitle(),
                })
              );
            } else {
              this.alertService.showToast(
                translate('configuration-item-editor-component.delete-success-taxonomy', {
                  count: this.totalItems,
                  itemTypeTitle: this.itemTypeTitle(),
                })
              );
            }

            this.totalItems = deleteResult.length;
            this.currentSearchIndex.update(() => -1);

            this.search$.next('');

            this.allItemsDeleted.emit(deleteResult);
            this.selectItem(undefined);

            this.isSaving = false;
            this.isLoading = false;
            this.itemName.enable();
            this.saving.emit(this.isSaving);
          } catch (error) {
            this.alertService.showErrorToast(
              translate('configuration-item-editor-component.failed-to-delete', {
                itemTypeTitle: this.itemTypeTitle,
              }),
              error
            );

            this.isSaving = false;
            this.isLoading = false;
            this.itemName.enable();
            this.saving.emit(this.isSaving);
          }

          try {
            if (this.evaluationConfigID) {
              await this.deleteAllItemsApi(this.evaluationConfigID);
            }
          } catch (error) {
            this.logger.error('Failed to delete evaluation config item', { error });
          }
        }
      });
  }

  async checkAndSelectItem(item, scrollTo?): Promise<boolean> {
    if (!this.selectedItem() || !item || this.selectedItem() !== item) {
      const proceed: boolean = await new Promise<boolean>((resolve) =>
        this.checkItemChanges(resolve)
      );

      if (proceed) {
        this.selectItem(item, scrollTo);
      }

      return proceed;
    }

    return true;
  }

  selectItem(item, scrollTo?) {
    this.editorMode = 'none';
    this.editorTemplateHasChanges.set(false);

    this.query = '';
    this.originalQuery = '';
    this.originalName = '';

    if (this.nameEditable()) {
      this.itemName.setValidators(
        configItemNameValidatorFactory(this.configItems, item, [
          this.namePropName(),
          this.queryPropName(),
        ])
      );
      this.itemName.setValue('');
    }

    this.selectedItem.set(item);

    if (this.selectedItem()) {
      this.queryPropHandler = new NoopQueryHandler();

      if (this.queryEditable()) {
        this.queryPropHandler = new StringQueryHandler();

        if (Array.isArray(this.selectedItem()[this.queryPropName()])) {
          this.queryPropHandler = new ArrayQueryHandler();
        }
      }

      if (this.selectedItem().id) {
        this.editorMode = 'update';
        this.inlineEditorMode = 'view';
      } else {
        this.editorMode = 'create';
        this.inlineEditorMode = 'edit';
        this.itemName.markAsDirty();
        if (!this.selectedItem()[this.queryPropName()]) {
          this.selectedItem()[this.queryPropName()] = this.queryPropHandler.parse('');
        }
      }

      this.query = this.queryPropHandler.serialize(this.selectedItem()[this.queryPropName()]);

      this.originalItem = JSON.parse(JSON.stringify(this.selectedItem()));
      this.originalQuery = this.query;
      this.originalName = this.selectedItem()[this.namePropName()] || this.query;
      this.itemName.setValue(this.originalName);

      this.selectedItemLayer = this.configurationLayers()?.find(
        (l) => l.id === this.selectedItem().config_id
      );

      if (this.currentSearchIndex() > -1) {
        this.currentSearchIndex.update(() => this.filteredConfigItems.indexOf(this.selectedItem()));
      }

      if (this.editorMode === 'create') {
        scrollTo = true;
      }

      if (scrollTo) {
        this.scrollToItem(item);
      }
    }

    this.selectedItemChange.emit(this.selectedItem());
    this.isTestButtonDisabled$.next(this.isTestButtonDisabled);
  }

  onScrollDown() {
    if (!this.isLoadingPage && this.page + 1 < this.totalPages) {
      const renderedRange = this.virtualScroll().getRenderedRange();
      //manual infinite scrolling.
      const end = renderedRange.end + 10;
      const total = this.configItems.length;
      if (end > total) {
        this.isLoadingPage = true;
        this.page++;
        this.loadItems(this.loadItemsApi)
          .then((res) => {
            this.configItems = [...this.configItems, ...res];
            this.filteredConfigItems = [...this.configItems];
          })
          .finally(() => (this.isLoadingPage = false));
      }
    }
  }

  async checkItemChanges(resolve) {
    this.itemCheckInProgress = true;

    let confirmTitle;
    let contentSuffix = translate('configuration-item-editor-component.content-suffix');
    if (this.isValid) {
      contentSuffix = translate('configuration-item-editor-component.content-suffix-ask');
      confirmTitle = translate('configuration-item-editor-component.confirm-title-ask');
    }

    if (this.hasChanges) {
      this.dialogService
        .showSimpleDialog({
          title: translate('configuration-item-editor-component.unsaved-changes'),
          content: translate('configuration-item-editor-component.unsaved-content', {
            itemTypeTitle: this.itemTypeTitle(),
            contentSuffix,
          }),
          confirmTitle,
          confirmColor: SimpleDialogButtonColor.green,
          cancelTitle: translate('configuration-item-editor-component.unsaved-cancel'),
          buttons: [
            {
              title: translate('configuration-item-editor-component.discard-changes'),
              color: SimpleDialogButtonColor.orange,
              handler: (dialogRef) => {
                dialogRef.close();
                this.cancelEdit();
                this.itemCheckInProgress = false;
                resolve(true);
              },
            },
          ],
        })
        .afterClosed()
        .subscribe(async (result) => {
          this.itemCheckInProgress = false;
          if (result) {
            const res = await this.saveEdit();
            resolve(res);
          } else {
            resolve(false);
          }
        });
    } else {
      this.itemCheckInProgress = false;
      resolve(true);
    }
  }

  deleteItem() {
    this.dialogService
      .showWarningPopup({
        title: translate('configuration-item-editor-component.delete-item', {
          itemTypeTitle: this.itemTypeTitle(),
        }),
        content: translate('configuration-item-editor-component.delete-item-content', {
          itemTypeTitle: this.itemTypeTitle(),
          prop: this.namePropName(),
        }),
        confirmTitle: translate('configuration-item-editor-component.delete-item-confirm', {
          itemTypeTitle: this.itemTypeTitle(),
        }),
        confirmColor: SimpleDialogButtonColor.red,
        cancelTitle: translate('configuration-item-editor-component.cancel'),
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.isSaving = true;
          this.isLoading = true;
          this.itemName.disable();
          this.saving.emit(this.isSaving);
          const deletedItemId = this.selectedItem().id;

          this.alertService.showToast(
            `Deleting ${this.itemTypeTitle()} ${this.selectedItem()[this.namePropName()]}…`
          );
          this.alertService.showToast(
            translate('configuration-item-editor-component.delete-item-process', {
              itemTypeTitle: this.itemTypeTitle(),
              prop: this.selectedItem()[this.namePropName()],
            })
          );
          try {
            await this.deleteItemApi(this.configuration().id, this.selectedItem().id);

            this.itemsDeleted.emit([this.selectedItem()]);
            this.totalItems--;

            this.configItems = this.configItems.filter((t) => t.id !== deletedItemId);
            this.filteredConfigItems = this.filteredConfigItems.filter(
              (t) => t.id !== deletedItemId
            );
            this.itemsLoaded.emit(this.configItems);
            this.selectItem(undefined);

            this.isSaving = false;
            this.isLoading = false;
            this.itemName.enable();
            this.saving.emit(this.isSaving);
            this.localStorageService.setItem('itemChanged', 'delete');
          } catch (error) {
            this.alertService.showErrorToast(
              translate('configuration-item-editor-component.failed-to-delete', {
                itemTypeTitle: this.itemTypeTitle,
              }),
              error
            );

            this.isSaving = false;
            this.isLoading = false;
            this.itemName.enable();
            this.saving.emit(this.isSaving);
          }
          //Delete the item from the evaluation config as well.
          try {
            if (this.evaluationConfigID) {
              const clonedItem = await this.getEvaluationItemByClonedFrom(
                this.evaluationConfigID,
                deletedItemId
              );
              if (clonedItem) {
                await this.deleteItemApi(this.evaluationConfigID, clonedItem.id);
              }
            }
          } catch (error) {
            this.logger.error('Failed to delete evaluation config item', { error });
            //Don't fail the rest of the variable settings because of an eval item
          }
        }
      });
  }

  cancelEdit() {
    if (this.originalItem) {
      if (this.originalItem.id) {
        Object.keys(this.selectedItem()).forEach((k) => {
          this.selectedItem()[k] = this.originalItem[k];
        });
        this.query = this.originalQuery;

        if (this.nameEditable()) {
          this.itemName.setValue(
            this.selectedItem()[this.namePropName()] || this.selectedItem()[this.queryPropName()]
          );
        }

        this.editorTemplateHasChanges.set(false);
      } else {
        this.selectItem(undefined);
      }
    }
  }

  public convertItemTypeToTranslatedVersion(itemType: string): string {
    return translate('analytic-items.' + itemType.toLowerCase().replace(' ', '-'));
  }

  async saveEdit(): Promise<boolean> {
    this.isSaving = true;
    this.isLoading = true;
    this.itemName.disable();
    this.saving.emit(this.isSaving);

    this.setPropsBeforeSave();

    try {
      let evalConfigItem;
      this.prepareSelectedItemSamples(this.selectedItem());
      if (this.selectedItem().id) {
        const updatedItem = await this.saveItemApi(this.configuration().id, this.selectedItem());
        const newSelectedItem = this.selectedItem();
        newSelectedItem.modification_date = updatedItem.modification_date;
        this.selectedItem.set(newSelectedItem);
        this.itemUpdated.emit(this.selectedItem());
        evalConfigItem = JSON.parse(JSON.stringify(this.selectedItem()));
      } else {
        const newSelectedItem = await this.createItemApi(
          this.configuration().id,
          this.selectedItem()
        );
        this.selectedItem.set(newSelectedItem);
        this.configItems.push(this.selectedItem());
        this.filteredConfigItems.push(this.selectedItem());
        this.itemAdded.emit(this.selectedItem());
        this.totalItems++;
        evalConfigItem = JSON.parse(JSON.stringify(this.selectedItem()));
        evalConfigItem['id'] = undefined;
      }

      evalConfigItem['cloned_from'] = this.selectedItem().id;
      try {
        if (this.itemTypeTitle() !== 'Blacklist item') {
          this.propagateItemToEvaluationConfig(evalConfigItem);
        }
      } catch (error) {
        //we don't want to fail to save to the real config because of any issue in the evaluation config.
        //do nothing
        this.logger.error('Failed to update evaluation config on item save', { error });
      }
      this.selectItem(this.selectedItem(), true);

      this.configItems.sort(this.itemSort.sortFn);
      this.filteredConfigItems = [...this.filteredConfigItems.sort(this.itemSort.sortFn)];

      this.scrollToItem(this.selectedItem());

      this.alertService.showToast(
        translate('configuration-item-editor-component.item-saved', {
          itemTypeTitle: this.itemTypeTitle(),
          prop: this.selectedItem()[this.namePropName()],
        })
      );
      this.localStorageService.setItem('itemChanged', 'saving');

      return true;
    } catch (error) {
      this.logger.error('Failed to update evaluation config on item save', { error });
      let message = translate('configuration-item-editor-component.failed-to-save', {
        itemTypeTitle: this.itemTypeTitle(),
        prop: this.selectedItem()[this.namePropName()],
      });
      const errorMsg = error && error.error && error.error.error;
      if (errorMsg) {
        if (errorMsg.indexOf('already present') !== -1) {
          message = translate('configuration-item-editor-component.failed-add-exists', {
            itemTypeTitle: this.itemTypeTitle,
            message,
          });
        }
        if (errorMsg.indexOf('queries are invalid') !== -1) {
          message = translate('configuration-item-editor-component.failed-add-syntax', {
            message,
          });

          // Error message from Semantria is a bit exotic
          const innerMatch = errorMsg.match(/lxaException \[\{"error_message": "(.*?)"/);
          let bracketContent = innerMatch?.[1]?.slice(0, 128) ?? null;

          if (bracketContent) {
            const errorOffset = errorMsg.match(/error_char_offset": (\d+)/)?.[1] ?? 0;
            if (bracketContent.startsWith('lxaException')) {
              bracketContent = bracketContent.match(/lxaException \[(.*)\]/, '')[1];
            }
            showTooltipAtPosition(
              this.codeEditor().view,
              +errorOffset,
              bracketContent.slice(0, 128)
            );
          }
        }
      }

      this.alertService.showErrorToast(message, error);

      return false;
    } finally {
      this.isSaving = false;
      this.isLoading = false;
      this.itemName.enable();
      this.saving.emit(this.isSaving);
    }
  }

  localeSort(a, b): number {
    const propA = a[this.namePropName()] || a[this.queryPropName()];
    const propB = b[this.namePropName()] || b[this.queryPropName()];
    return propA.localeCompare(propB);
  }

  prepareSelectedItemSamples(item) {
    if (item.hasOwnProperty('samples')) {
      if (Array.isArray(item['samples'])) {
        item['samples'] = item['samples'].join(',');
      }
      item['samples'] = item['samples'].split(/[\n,]+/);
    }
  }

  protected setPropsBeforeSave() {
    this.selectedItem()[this.namePropName()] = this.itemName.value;
    this.selectedItem()[this.queryPropName()] = this.queryPropHandler.parse(this.query);
  }

  get hasChanges() {
    return (
      this.editorMode === 'create' ||
      this.editorTemplateHasChanges() ||
      (this.queryEditable() && this.query !== this.originalQuery) ||
      (this.nameEditable() && this.itemName.value !== this.originalName)
    );
  }

  get isValid() {
    return (
      this.editorMode === 'none' ||
      (this.isEditorTemplateValid() &&
        (!this.queryEditable() || (this.query && this.query.trim())) &&
        (!this.nameEditable() ||
          !this.selectedItem() ||
          this.itemName.disabled ||
          this.itemName.valid))
    );
  }

  //Either creates or updates a configuration item based on whether it exits.
  //Returns the following
  //
  // 1. The item that previously existed in the configuration.  undefined if this is a new item
  // 2. The item updated or created in the evaluation configuration
  // 3. Boolean for if an item was updated or not.
  // 4. Any Error that occurred.
  //
  async propagateItemToEvaluationConfig(clonedItem): Promise<EvaluationConfigUpdateResponse> {
    this.evaluationConfigID = await this.getEvaluationConfigApi(this.configuration());
    const results: EvaluationConfigUpdateResponse = {
      existingConfigItem: undefined,
      updatedConfigItem: undefined,
      wasUpdated: false,
      error: undefined,
    };
    if (clonedItem.id) {
      try {
        results.existingConfigItem = await this.getEvaluationItemByClonedFrom(
          this.evaluationConfigID,
          this.selectedItem().id
        );
      } catch (error) {
        this.logger.error('Failed to get evaluation item by cloned from', { error });
        //Not found not a problem
      }
    }
    if (results.existingConfigItem) {
      clonedItem['id'] = results.existingConfigItem.id;
      //Don't update if there is no difference
      if (
        results.existingConfigItem[this.queryPropName()] !== clonedItem[this.queryPropName()] ||
        results.existingConfigItem['weight'] !== clonedItem['weight']
      ) {
        try {
          results.updatedConfigItem = await this.saveItemApi(this.evaluationConfigID, clonedItem);
          results.wasUpdated = true;
        } catch (error) {
          results.error = error;
        }
      } else {
        results.updatedConfigItem = results.existingConfigItem;
      }
    } else {
      clonedItem['id'] = undefined;
      try {
        results.updatedConfigItem = await this.createItemApi(this.evaluationConfigID, clonedItem);
      } catch (error) {
        results.error = error;
      }
    }
    return results;
  }

  //Evaluates the current text of the editor window.
  //Sends the current value to an evaluation config
  //Gets all of the documents from the textboxes, evaluates, then runs highlighting.
  //TODO: currently every time the new element or updated element are deleted or reverted respectively.
  //This might not be the best way to do it.
  async evaluateItem(attemptNumber = 0) {
    this.isEvaluationInProgress = true;

    // EV METHOD 1: Evaluate dataset documents
    if (this.isDatasetTestTabActive) {
      try {
        this.alertService.showToast(translate('configuration-item-editor-component.evaluating'));

        const selectedItemClone = { ...this.selectedItem() };
        // Use current query for evaluation
        if ('query' in selectedItemClone) {
          selectedItemClone.query = this.query;
        }
        await this.evaluateDatasetApi(this.configuration().id, selectedItemClone);
      } catch (e) {
        this.alertService.showErrorToast(
          translate('configuration-item-editor-component.failed-to-eval'),
          e
        );
        this.isEvaluationInProgress = false;
      }
      // Important to return here as we don't want to execute evaluation method 2
      return;
    }

    // EV METHOD 2: Evaluate created documents
    this.clearAllSpans(this.testDocuments);
    this.evaluationConfigID = await this.getEvaluationConfigApi(this.configuration());
    try {
      if (
        (this.itemTypeTitle() === 'Sentiment Phrase' || this.itemTypeTitle() === 'Concept Topic') &&
        this.selectedItem()['weight'] === 0
      ) {
        const weightOrBoost = this.itemTypeTitle() === 'Sentiment Phrase' ? 'weight' : 'boost';
        this.alertService.showToast(
          translate('configuration-item-editor-component.zero-boost-never-hit', {
            itemTypeTitle: this.itemTypeTitle(),
            boost: weightOrBoost,
          })
        );
        this.isEvaluationInProgress = false;
        return;
      }
      this.alertService.showToast(translate('configuration-item-editor-component.evaluating'));
      const clonedItem = JSON.parse(JSON.stringify(this.selectedItem()));

      //Create the temp object
      clonedItem[this.namePropName()] = this.itemName.value;
      clonedItem[this.queryPropName()] = this.queryPropHandler.parse(this.query);
      clonedItem['cloned_from'] = this.selectedItem().id;
      this.prepareSelectedItemSamples(clonedItem);
      const documentsToRun = this.setupAnalysisDocuments(this.testDocuments);
      if (documentsToRun.length > 0) {
        const cloneItemPropagateResults = await this.propagateItemToEvaluationConfig(clonedItem);

        //Check for an error in setting up the item.
        //If it's a 409 then it's a duplicate somehow.
        if (cloneItemPropagateResults.error) {
          this.logger.error('Propagate Error', { error: cloneItemPropagateResults.error });
          let errorMessage = translate('configuration-item-editor-component.error-message-prepare');
          if (cloneItemPropagateResults.error.status === 409) {
            errorMessage = errorMessage.concat(
              translate('configuration-item-editor-component.error-message-add')
            );
          }
          this.alertService.showErrorToast(errorMessage);
          this.isEvaluationInProgress = false;
          return;
        }
        this.createdEvaluationItemId = cloneItemPropagateResults.updatedConfigItem
          ? cloneItemPropagateResults.updatedConfigItem.id
          : undefined;
        this.evaluationResults = await this.evaluateItemApi(
          this.evaluationConfigID,
          documentsToRun
        );
        this.applyHighlights(
          this.evaluationResults,
          this.getEvaluationItemHighlightValue(cloneItemPropagateResults.updatedConfigItem)
        );
        //TODO: think if we really need to do this.
        //Delete the created temp object
        if (this.editorMode === 'create') {
          await this.deleteItemApi(this.evaluationConfigID, this.createdEvaluationItemId);
          this.createdEvaluationItemId = undefined;
        } else {
          //Restore the item to what it was before testing
          //Only will happen if there was an update to be undone
          if (
            cloneItemPropagateResults.existingConfigItem &&
            cloneItemPropagateResults.wasUpdated
          ) {
            await this.saveItemApi(
              this.evaluationConfigID,
              cloneItemPropagateResults.existingConfigItem
            );
          }
        }
        this.testDocuments.forEach((doc) => {
          doc.hasBeenTested = true;
        });
        this.alertService.showToast(translate('configuration-item-editor-component.evaluated'));
      } else {
        this.alertService.showToast(
          translate('configuration-item-editor-component.no-docs-to-eval')
        );
      }
      this.isEvaluationInProgress = false;
    } catch (error) {
      //If there's a failure for some reason.  Delete the evaluation config and try fresh.
      //There could potentially be a way that the config got out of sorts.
      if (attemptNumber < 1) {
        this.logger.error(
          'Failed first attempt with evaluation config: ' + this.evaluationConfigID,
          { error }
        );
        await this.deleteEvaluationConfigApi(this.evaluationConfigID);
        this.evaluationConfigID = await this.getEvaluationConfigApi(this.configuration());
        this.logger.info('Retrying with evaluation config: ' + this.evaluationConfigID);
        this.evaluateItem(1);
      } else {
        this.alertService.showErrorToast(
          translate('configuration-item-editor-component.failed-to-eval'),
          error
        );
        this.isEvaluationInProgress = false;
      }
    }
  }

  setupAnalysisDocuments(documents: any[]): SemantriaDocumentRequest[] {
    const results = [];
    documents.forEach((doc) => {
      const documentDiv = document.getElementById('test-document-' + doc.documentId);
      if (documentDiv) {
        const text = documentDiv.innerText;
        if (text.trim().length > 0) {
          results.push({ id: doc.documentId, text });
        }
      }
    });
    return results;
  }

  applyHighlights(documentResults: DocumentAnalysis[], itemValues: string[]) {
    documentResults.forEach((doc) => {
      const documentDiv = document.getElementById('test-document-' + doc.id);
      this.clearTestDocumentSpan(documentDiv);
      const text = this.getHighlightTextString(doc, documentDiv.innerText, itemValues);
      documentDiv.innerHTML = text;
    });
  }

  clearAllSpans(documents: any[]) {
    documents.forEach((doc) => {
      const documentDiv = document.getElementById('test-document-' + doc.documentId);
      if (documentDiv) {
        this.clearSpans({ target: documentDiv });
      }
    });
  }

  //Run to clear the highlights when the text is altered in the document.
  clearSpans($event) {
    const documentId = $event.target.id.replace('test-document-', '');
    this.testDocuments.forEach((doc) => {
      if (doc.documentId === documentId) {
        doc.hasBeenTested = false;
        doc.canRephrase = !!$event.target.innerText;
      }
    });
    this.testDocumentsChanged();

    const checkMarkItem = document.getElementById('evaluation-checkmark-' + documentId);
    if (
      checkMarkItem &&
      (checkMarkItem.classList.contains('configuration-item-editor__evaluation-checkmark--hit') ||
        checkMarkItem.classList.contains('configuration-item-editor__evaluation-checkmark--miss'))
    ) {
      checkMarkItem.classList.remove('configuration-item-editor__evaluation-checkmark--hit');
      checkMarkItem.classList.remove('configuration-item-editor__evaluation-checkmark--miss');
      checkMarkItem.title = '';

      const currentCursor = this.getCursorCharacterOffsetWithin($event.target);
      this.clearTestDocumentSpan($event.target);
      setTimeout(() => {
        this.setCursor($event.target, currentCursor);
      }, 50);
    }
  }

  //Captures the current cursor location so that when we clear the spans
  //We can go back there.
  getCursorCharacterOffsetWithin(element) {
    let caretOffset = 0;
    const doc = element.ownerDocument || element.document;
    const win = doc.defaultView || doc.parentWindow;
    let sel;
    if (typeof win.getSelection !== 'undefined') {
      sel = win.getSelection();
      if (
        sel.anchorNode.parentElement === element &&
        sel.focusNode.parentElement === element &&
        sel.rangeCount > 0
      ) {
        const range = win.getSelection().getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        caretOffset = preCaretRange.toString().length;
      }
    }
    return caretOffset;
  }

  setCursor(el, position) {
    const range = document.createRange();
    const sel = window.getSelection();
    range.setStart(el.childNodes[0], position);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    el.focus();
  }

  clearTestDocumentSpan(documentDiv) {
    documentDiv.innerHTML = documentDiv.innerHTML.replace(/<\/?span[^>]*>/g, '');
  }

  removeDocument(i) {
    this.testDocuments.splice(i, 1);
    if (this.testDocuments.length === 0) {
      this.addEmptyDocument();
    }
    this.testDocumentsChanged();
  }

  onShowStemmedVersionModal(i: number) {
    const testDocument = this.testDocuments[i];
    let testDocumentEvaluation;
    for (const doc of this.evaluationResults) {
      if (doc.id === testDocument.documentId) {
        testDocumentEvaluation = doc;
      }
    }

    if (!testDocumentEvaluation.details || !testDocumentEvaluation.details[0].words) {
      this.alertService.showErrorToast(
        translate('configuration-item-editor-component.no-stemmed-words')
      );
      return;
    }

    const stemmedWords = testDocumentEvaluation.details[0].words;
    let stemmedText = '';
    for (const word of stemmedWords) {
      stemmedText = stemmedText.concat(' ' + word.stemmed);
    }
    this.stemmedDocumentData = {
      title: translate('configuration-item-editor-component.stemmed-version-title'),
      content: stemmedText,
      cancelButton: translate('configuration-item-editor-component.close'),
    };
    this.showStemmedDocumentModal = true;
  }

  onCloseStemmedVersionModal() {
    this.showStemmedDocumentModal = false;
  }

  onRephraseDocument(id: string): void {
    const testDocument = this.testDocuments.find((doc) => doc.documentId === id);
    const documentDiv = document.getElementById('test-document-' + testDocument.documentId);

    if (documentDiv) {
      testDocument.rephraseInProgress = true;
      this.testDocumentsChanged();
      const text = documentDiv.innerText;

      this.testDocumentGeneratorService.rephraseTestDocument(text).then(
        (result) => {
          if (result) {
            const docIndex = this.testDocuments.indexOf(testDocument);
            if (docIndex >= 0) {
              this.testDocuments.splice(docIndex, 1, {
                value: result,
                documentId: UUIDv4(),
                htmlContent: '',
                hasBeenTested: false,
                canRephrase: true,
                rephraseInProgress: false,
              });
              setTimeout(() => this.testDocumentsChanged());
            }
          }
        },
        (error: HttpErrorResponse | Error) => {
          this.alertService.showErrorToast(
            translate('configuration-item-editor-component.unable-to-rephrase'),
            error
          );
        }
      );
    }
  }

  addEmptyDocument() {
    this.addDocument('');
  }

  addDocument(document: string) {
    this.testDocuments.push({
      value: document,
      documentId: UUIDv4(),
      htmlContent: '',
      hasBeenTested: false,
      rephraseInProgress: false,
      canRephrase: !!document,
    });

    this.testDocumentsChanged();
  }

  setDocumentAt(document: string, index: number) {
    const newTestDoc = {
      value: document,
      documentId: UUIDv4(),
      htmlContent: '',
      hasBeenTested: false,
      rephraseInProgress: false,
      canRephrase: !!document,
    };

    if (index >= 0 && index < this.testDocuments.length) {
      this.testDocuments.splice(index, 1, newTestDoc);
    } else {
      this.testDocuments.push(newTestDoc);
    }

    this.testDocumentsChanged();
  }

  private testDocumentsChanged(): void {
    this.isTestButtonDisabled$.next(this.isTestButtonDisabled);
    this.cdRef.markForCheck();
  }

  private countBlankDocuments(): number {
    return this.testDocuments.reduce((acc, doc, i) => {
      if (this.isTestDocumentBlank(i)) {
        acc++;
      }

      return acc;
    }, 0);
  }

  private isTestDocumentBlank(index: number): boolean {
    return !this.testDocuments[index].value;
  }

  onDeleteAllDocuments(): void {
    this.testDocuments = [];
    this.addEmptyDocument();
  }

  onGenerateTestDocuments() {
    const maxCount =
      this.maxTestDocumentCount - this.testDocuments.length + this.countBlankDocuments();
    this.dialogService
      .showDialog(
        TestDocumentGeneratorDialogComponent,
        {
          maxCount,
          query: this.selectedItem()[this.queryPropName()],
        },
        {},
        false
      )
      .afterClosed()
      .pipe(takeUntil(this.unsub$))
      .subscribe((generatedTestDocs: string[] | null) => {
        if (generatedTestDocs?.length > 0) {
          generatedTestDocs.forEach((testDoc) => {
            const blankDocIndex = this.testDocuments.findIndex((doc, i) =>
              this.isTestDocumentBlank(i)
            );
            if (blankDocIndex >= 0) {
              this.setDocumentAt(testDoc, blankDocIndex);
            } else {
              this.addDocument(testDoc);
            }
          });
        }
      });
  }

  //Save the documents when we navigate back to the list of items so they can be loaded back.
  propagateDocs() {
    this.testDocuments.forEach((doc) => {
      const documentDiv = document.getElementById('test-document-' + doc.documentId);
      doc.value = documentDiv.innerText;
      doc.htmlContent = documentDiv.innerHTML;
    });
  }

  //Loads the documents back that have been saved to preserve html.
  loadDocumentHTML() {
    setTimeout(() => {
      this.testDocuments.forEach((doc) => {
        const documentDiv = document.getElementById('test-document-' + doc.documentId);
        if (doc.htmlContent) {
          documentDiv.innerHTML = doc.htmlContent;
        }
      });
    }, 100);
  }

  async loadDataset() {
    try {
      this.configTesterState = {
        ...this.configTesterState,
        loading: true,
      };
      const datasetDescriptor = await this.configurationsService.getDataset(
        this.configuration().id
      );
      this.setCTDatasetDescriptor(datasetDescriptor);
    } catch (error) {
      this.logger.error('getDataset failed', { error });
    } finally {
      this.configTesterState = {
        ...this.configTesterState,
        loading: false,
      };
    }
  }

  async loadResultsInfo() {
    try {
      this.configTesterState = {
        ...this.configTesterState,
        loading: true,
      };
      const evaluation = await this.configurationsService.getEvaluationDescriptor(
        this.selectedItem().id
      );

      this.onCTDocumentListResultsArrived(evaluation);
    } catch (error) {
      this.logger.error('getEvaluationDescriptor failed', { error });
    } finally {
      this.configTesterState = {
        ...this.configTesterState,
        loading: false,
      };
    }
  }

  async clearEvaluationConfig() {
    this.isClearingCache = true;
    if (this.evaluationConfigID) {
      await this.deleteEvaluationConfigApi(this.evaluationConfigID);
    }
    this.localStorageService.removeItem('evaluationConfigMap');
    this.createdEvaluationItemId = undefined;
    this.evaluationConfigID = await this.getEvaluationConfigApi(this.configuration());
    this.isClearingCache = false;
    this.alertService.showToast(translate('configuration-item-editor-component.cache-cleaned'));
  }

  onTestModeClicked() {
    this.isFullscreen.set(false);

    this.testMode = true;
    this.rightSplitSize = 30;
    this.leftSplitSize = 70;

    this.loadDocumentHTML();
    this.loadDataset();
    this.loadResultsInfo();
  }

  onEditModeClicked(propagate = true) {
    this.testMode = false;
    this.leftSplitSize = 30;
    this.rightSplitSize = 70;

    if (propagate) {
      this.propagateDocs();
    }
  }

  private get isTestButtonDisabled() {
    if (this.failedToCreateEvalConfig) {
      return true;
    }

    if (this.isDatasetTestTabActive) {
      return this.isDatasetTestTabMissingRequirements();
    }

    if (this.testDocuments.some((doc) => doc.rephraseInProgress)) {
      return true;
    }

    return !this.testDocuments.some((doc, index) => {
      return !this.isTestDocumentBlank(index);
    });
  }

  diabledTestButtonToolTip() {
    if (this.failedToCreateEvalConfig) {
      return translate('configuration-item-editor-component.failed-to-prepare', {
        failedMsg: this.failedEvalConfigMessage,
      });
    }
    if (this.isDatasetTestTabMissingRequirements()) {
      return translate('configuration-item-editor-component.save-item-before-test');
    }
    return translate('configuration-item-editor-component.no-documents');
  }

  newDocumentsButtonToolTip() {
    if (this.testDocuments.length >= this.maxTestDocumentCount) {
      return translate('configuration-item-editor-component.only-max-documents', {
        max: this.maxTestDocumentCount,
      });
    }
    return translate('configuration-item-editor-component.add-new-document');
  }

  generateDocumentsButtonToolTip() {
    if (this.testDocuments.length >= this.maxTestDocumentCount) {
      return translate('configuration-item-editor-component.only-max-documents', {
        max: this.maxTestDocumentCount,
      });
    }
    return translate('configuration-item-editor-component.generate-new-document');
  }

  get testModeButtonTooltip() {
    if (!this.testingEnabled()) {
      return translate('configuration-item-editor-component.test-button-mentions');
    }
  }

  getDeleteItemTooltip(itemId) {
    if (this.taxonomyItemUsage() && this.taxonomyItemUsage().includes(itemId)) {
      return translate('configuration-item-editor-component.taxonomy-node-part', {
        itemTypeTitle: this.itemTypeTitle(),
      });
    }
  }

  get editDefinitionTooltip() {
    if (this.selectedItemLayer?.isLanguageTemplate) {
      return translate('configuration-item-editor-component.language-templates');
    }
    if (this.selectedItemLayer?.isIndustryPack) {
      return translate('configuration-item-editor-component.industry-packs');
    }
  }

  onEditDefintionClicked() {
    const queryParams = {
      itemName: this.selectedItem()[this.namePropName()],
      itemId: this.selectedItem().id,
    } as any;

    if ((this.selectedItem() as any).type) {
      queryParams.itemType = (this.selectedItem() as any).type;
    }

    const urlTree = this.router.createUrlTree(
      [
        'groups',
        this.groupService.getSelectedGroupId(true),
        'configurations',
        this.selectedItemLayer.id,
      ],
      {
        queryParams,
        fragment: this.fragment(),
      }
    );

    window.open(this.router.serializeUrl(urlTree));
  }

  onOpenOptionsPopout(): void {
    this.isOptionsPopoutVisible = true;
  }

  onCloseOptionsPopout(): void {
    const original = this.configurationItemListOptionsService.get();
    if (!this.configurationItemListOptions.equals(original)) {
      this.configurationItemListOptionsService.set(this.configurationItemListOptions);

      this.loadByConfigurationItemListOptions();
    }

    this.isOptionsPopoutVisible = false;
  }

  private loadByConfigurationItemListOptions(): void {
    this.applyConfigurationItemListOptions();

    this.onSearchTypeChanged();
  }

  private applyConfigurationItemListOptions(): void {
    this.applyConfigurationItemListOptionsSort();

    this.applyConfigurationItemListOptionsFilter();
  }

  private applyConfigurationItemListOptionsSort(): void {
    switch (this.configurationItemListOptions.sort) {
      case 'name':
        this.itemSort = this.itemSortValues[0];
        break;
      case 'date':
        this.itemSort = this.itemSortValues[1];
        break;
    }
  }

  private applyConfigurationItemListOptionsFilter(): void {
    switch (this.configurationItemListOptions.filter) {
      case 'both':
        this.searchType = 'all';
        break;
      case 'name':
        this.searchType = 'name';
        break;
      case 'query':
        this.searchType = 'query';
        break;
    }
  }

  private isDatasetTestTabMissingRequirements(): boolean {
    return (
      this.isDatasetTestTabActive && (!this.configTesterState.dataset || !this.selectedItem()?.id)
    );
  }

  toggleFullscreen(): void {
    this.isFullscreen.set(!this.isFullscreen());
  }

  get fullscreenIcon(): string {
    return this.isFullscreen() ? 'invalid' : 'full_screen';
  }

  get fullscreenTooltip(): string {
    const exitKey = 'exit-fullscreen';
    const enterKey = 'enter-fullscreen';

    if (this.isFullscreen()) {
      return exitKey;
    } else {
      return enterKey;
    }
  }

  get isDatasetTestTabActive() {
    return this.configTesterState.selectedTab === 'use-dataset';
  }
}
